import React from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const TrendChart = ({
  title,
  data,
  dataKeys = [],
  xAxisKey = 'name',
  height = 300,
  type = 'line', // 'line' or 'area'
  colors = ['#003366', '#D4AF37', '#4A90E2', '#50C878'],
  loading = false,
  yAxisFormat = 'number'
}) => {
  const formatYAxis = (value) => {
    switch (yAxisFormat) {
      case 'currency':
        return `$${(value / 1000).toFixed(0)}k`;
      case 'percent':
        return `${value}%`;
      case 'number':
      default:
        return value.toLocaleString();
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 1.5,
            boxShadow: 2
          }}
        >
          <Typography variant="body2" fontWeight="bold" mb={1}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Box key={index} display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: entry.color,
                  borderRadius: '50%'
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {entry.name}:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {yAxisFormat === 'currency'
                  ? `$${entry.value.toLocaleString()}`
                  : yAxisFormat === 'percent'
                  ? `${entry.value}%`
                  : entry.value.toLocaleString()}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>{title}</Typography>
          <Box display="flex" alignItems="center" justifyContent="center" height={height}>
            <Typography color="text.secondary">Loading...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const ChartComponent = type === 'area' ? AreaChart : LineChart;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          {title}
        </Typography>
        <ResponsiveContainer width="100%" height={height}>
          <ChartComponent data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis
              dataKey={xAxisKey}
              tick={{ fontSize: 12 }}
              stroke="#666"
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={{ fontSize: 12 }}
              stroke="#666"
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="circle"
            />
            {dataKeys.map((key, index) => {
              const color = colors[index % colors.length];
              if (type === 'area') {
                return (
                  <Area
                    key={key.dataKey || key}
                    type="monotone"
                    dataKey={key.dataKey || key}
                    name={key.name || key}
                    stroke={color}
                    fill={color}
                    fillOpacity={0.3}
                    strokeWidth={2}
                  />
                );
              }
              return (
                <Line
                  key={key.dataKey || key}
                  type="monotone"
                  dataKey={key.dataKey || key}
                  name={key.name || key}
                  stroke={color}
                  strokeWidth={2}
                  dot={{ fill: color, r: 4 }}
                  activeDot={{ r: 6 }}
                />
              );
            })}
          </ChartComponent>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default TrendChart;
